import React from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";
import { useLLM } from "@/contexts/LLMContext";

/**
 * What went wrong, in words, and what to do about it.
 *
 * Failures used to surface as a toast carrying whatever string came to hand -
 * often "Request failed with status code 428", which names neither the problem
 * nor the remedy and disappears before it can be read twice. The server sends
 * a message and a suggested action for every failure it knows about; this is
 * where they are shown.
 */

// Said in the reader's terms, for the failures that have no server-side text.
const FALLBACKS = {
  network: {
    title: "NUMA non raggiunge il server",
    detail: "Controlla la connessione. Il testo che hai scritto è salvato sul tuo dispositivo e non va perso.",
  },
  timeout: {
    title: "Il modello ci sta mettendo troppo",
    detail: "La richiesta è stata interrotta. Riprova, oppure lavora su una porzione di testo più breve.",
  },
  rate_limit: {
    title: "Troppe richieste ravvicinate",
    detail: "Il provider ha messo in pausa le chiamate. Aspetta un minuto e riprova.",
  },
  context_too_long: {
    title: "Il testo è troppo lungo per questo modello",
    detail: "Seleziona una parte più breve, oppure collega un modello con una finestra di contesto più ampia.",
  },
  validation: { title: "Un dato non è valido", detail: "" },
  error: { title: "Qualcosa non ha funzionato", detail: "" },
};

export default function ErrorDialog() {
  const { failure, dismissError, openKeyDialog } = useLLM();
  if (!failure) return null;

  const fallback = FALLBACKS[failure.code] || FALLBACKS.error;
  const title = failure.code === "validation" ? fallback.title : fallback.title;
  const message = failure.message || fallback.detail;
  const action = failure.action || (failure.message ? fallback.detail : "");

  return (
    <Dialog open onOpenChange={(open) => !open && dismissError()}>
      <DialogContent data-testid="error-dialog" className="max-w-md rounded-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-serif-display text-lg">
            <AlertTriangle className="h-4 w-4 shrink-0 text-warn" />
            {title}
          </DialogTitle>
          <DialogDescription className="sr-only">Dettaglio dell&apos;errore</DialogDescription>
        </DialogHeader>

        <p className="text-[13.5px] leading-relaxed text-ink">{message}</p>
        {action && <p className="numa-meta leading-relaxed">{action}</p>}
        {failure.provider && (
          <p className="text-2xs text-ink-subtle">Provider: {failure.provider}</p>
        )}
        {failure.raw && (
          <details className="rounded-md border border-line bg-surface-2 px-3 py-2">
            <summary className="cursor-pointer text-2xs text-ink-muted">
              Dettaglio tecnico
            </summary>
            <p className="mt-1.5 break-words font-mono text-[11px] leading-relaxed text-ink-muted">
              {String(failure.raw).slice(0, 600)}
            </p>
          </details>
        )}

        <div className="flex flex-wrap justify-end gap-2">
          {failure.offreCollegamento && (
            <Button
              data-testid="error-connect-model"
              onClick={() => { dismissError(); openKeyDialog(failure); }}
            >
              Collega un modello
            </Button>
          )}
          <Button variant={failure.offreCollegamento ? "outline" : "default"} onClick={dismissError}>
            Ho capito
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
